import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import Button from '../../components/common/Button';
import Spinner from '../../components/common/Spinner';
import listasService from '../../services/listasProveedor.service';
import comparadorService from '../../services/comparadorPrecios.service';
import proveedoresService from '../../services/proveedores.service';

const campo = 'nexo-field';

function dinero(valor) {
  if (valor === null || valor === undefined || Number.isNaN(Number(valor))) return '—';
  return Number(valor).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function etiquetaSentido(sentido) {
  if (sentido === 'sube') return 'Sube';
  if (sentido === 'baja') return 'Baja';
  if (sentido === 'igual') return 'Igual';
  if (sentido === 'entro') return 'Entró a la lista';
  if (sentido === 'salio') return 'Salió de la lista';
  return 'No calculable';
}

export default function ComparadorPrecios() {
  const [cargando, setCargando] = useState(true);
  const [comparando, setComparando] = useState(false);
  const [errorCarga, setErrorCarga] = useState('');
  const [listas, setListas] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [seleccion, setSeleccion] = useState([]);
  const [fecha, setFecha] = useState(() => new Date().toISOString().slice(0, 10));
  const [factor, setFactor] = useState({ desde: '', hacia: '', factor: '', verificable: false });
  const [cotizacion, setCotizacion] = useState({ origen: 'USD', destino: 'ARS', valor: '', fecha: '', verificable: false });
  const [resultado, setResultado] = useState(null);
  const [filtroFamilia, setFiltroFamilia] = useState('');
  const [filtroProveedor, setFiltroProveedor] = useState('');
  const [filtroVista, setFiltroVista] = useState('todas');
  const [cantidades, setCantidades] = useState({});

  const cargar = useCallback(async () => {
    setCargando(true);
    setErrorCarga('');
    const [listasRes, provRes] = await Promise.allSettled([
      listasService.listar(),
      proveedoresService.obtenerTodos()
    ]);
    if (listasRes.status === 'fulfilled') setListas(listasRes.value);
    else {
      setListas([]);
      const mensaje = listasRes.reason?.message || 'No se pudieron leer las listas';
      setErrorCarga(mensaje);
      toast.error(mensaje);
    }
    setProveedores(provRes.status === 'fulfilled' && Array.isArray(provRes.value) ? provRes.value : []);
    setCargando(false);
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const nombreProveedor = useCallback((id) => {
    const hallado = proveedores.find((item) => item.id === id);
    return hallado?.nombre || hallado?.razon_social || id;
  }, [proveedores]);

  const comparar = async () => {
    if (!seleccion.length) {
      toast.error('Elegí al menos una lista');
      return;
    }
    setComparando(true);
    try {
      const factores = factor.factor
        ? [{
          desde: factor.desde,
          hacia: factor.hacia,
          factor: Number(factor.factor),
          verificable: factor.verificable === true
        }]
        : [];
      const cotizaciones = cotizacion.valor
        ? [{
          origen: cotizacion.origen,
          destino: cotizacion.destino,
          valor: Number(cotizacion.valor),
          fecha: cotizacion.fecha,
          verificable: cotizacion.verificable === true
        }]
        : [];
      const simulacion = Object.entries(cantidades)
        .filter(([, cantidad]) => Number(cantidad) > 0)
        .map(([clave, cantidad]) => {
          const [producto_id, calidad] = clave.split('::');
          return { producto_id, calidad, cantidad: Number(cantidad) };
        });
      const data = await comparadorService.consultar({
        lista_ids: seleccion,
        fecha_consulta: fecha,
        moneda_objetivo: 'ARS',
        factores,
        cotizaciones,
        simulacion
      });
      setResultado(data);
    } catch (error) {
      toast.error(error.message || 'No se pudo comparar');
    } finally {
      setComparando(false);
    }
  };

  const familias = useMemo(() => {
    const valores = new Set((resultado?.grupos || []).map((grupo) => grupo.familia).filter(Boolean));
    return [...valores];
  }, [resultado]);

  const grupos = (resultado?.grupos || []).filter((grupo) => {
    if (filtroFamilia && grupo.familia !== filtroFamilia) return false;
    if (filtroProveedor && !grupo.ofertas.some((oferta) => oferta.proveedor_id === filtroProveedor)) return false;
    return true;
  });
  const noComparables = (resultado?.no_comparables || []).filter((item) => (
    !filtroProveedor || item.proveedor_id === filtroProveedor
  ));
  const variaciones = (resultado?.variaciones || []).filter((item) => (
    !filtroProveedor || item.proveedor_id === filtroProveedor
  ));
  const verCostos = resultado?.finanzas_visibles?.costos === true;
  const verMargenes = resultado?.finanzas_visibles?.margenes === true;

  if (cargando) return <div className="p-6"><Spinner /></div>;

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-4 sm:p-6">
      <div>
        <h1 className="text-2xl font-bold text-e0e3-foreground">Comparador de precios</h1>
        <p className="mt-1 text-sm text-e0e3-muted">
          Compara listas versionadas y equivalencias confirmadas. No crea compras ni cambia costos, precios, stock ni caja.
        </p>
        <p className="mt-1 text-sm text-e0e3-muted">El precio más bajo no indica mejor calidad.</p>
      </div>

      {errorCarga ? <p className="text-sm text-e0e3-foreground">{errorCarga}</p> : null}

      <section className="nexo-surface space-y-4 rounded-2xl p-4">
        <h2 className="font-semibold text-e0e3-foreground">Listas</h2>
        <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
          {listas.map((item) => (
            <label key={item.id} className="flex items-start gap-2 text-sm text-e0e3-foreground">
              <input
                type="checkbox"
                className="mt-1"
                checked={seleccion.includes(item.id)}
                onChange={(event) => {
                  setSeleccion((actual) => (
                    event.target.checked ? [...actual, item.id] : actual.filter((id) => id !== item.id)
                  ));
                }}
              />
              <span>
                Versión {item.version} · {item.nombre_archivo || 'sin nombre'} · {nombreProveedor(item.proveedor_id)}
                <span className="block text-e0e3-muted">{String(item.creado_en || '').slice(0, 10)}</span>
              </span>
            </label>
          ))}
        </div>
        {!listas.length ? <p className="text-sm text-e0e3-muted">Esta empresa no tiene listas de proveedor.</p> : null}

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <label className="text-sm text-e0e3-foreground">
            Fecha de consulta
            <input className={`${campo} mt-1 w-full`} type="date" value={fecha} onChange={(event) => setFecha(event.target.value)} />
          </label>
          <label className="text-sm text-e0e3-foreground">
            Moneda objetivo
            <input className={`${campo} mt-1 w-full`} value="ARS" readOnly />
          </label>
          <label className="text-sm text-e0e3-foreground">
            Factor desde
            <input className={`${campo} mt-1 w-full`} value={factor.desde} placeholder="bolsa" onChange={(event) => setFactor({ ...factor, desde: event.target.value })} />
          </label>
          <label className="text-sm text-e0e3-foreground">
            Factor hacia
            <input className={`${campo} mt-1 w-full`} value={factor.hacia} placeholder="kg" onChange={(event) => setFactor({ ...factor, hacia: event.target.value })} />
          </label>
          <label className="text-sm text-e0e3-foreground">
            Factor verificable
            <input className={`${campo} mt-1 w-full`} inputMode="decimal" value={factor.factor} placeholder="10" onChange={(event) => setFactor({ ...factor, factor: event.target.value })} />
          </label>
          <label className="mt-6 flex items-center gap-2 text-sm text-e0e3-foreground">
            <input type="checkbox" checked={factor.verificable} onChange={(event) => setFactor({ ...factor, verificable: event.target.checked })} />
            El factor está verificado
          </label>
          <label className="text-sm text-e0e3-foreground">
            Cotización desde
            <input className={`${campo} mt-1 w-full`} value={cotizacion.origen} onChange={(event) => setCotizacion({ ...cotizacion, origen: event.target.value.toUpperCase() })} />
          </label>
          <label className="text-sm text-e0e3-foreground">
            Cotización hacia
            <input className={`${campo} mt-1 w-full`} value={cotizacion.destino} onChange={(event) => setCotizacion({ ...cotizacion, destino: event.target.value.toUpperCase() })} />
          </label>
          <label className="text-sm text-e0e3-foreground">
            Valor
            <input className={`${campo} mt-1 w-full`} inputMode="decimal" value={cotizacion.valor} onChange={(event) => setCotizacion({ ...cotizacion, valor: event.target.value })} />
          </label>
          <label className="text-sm text-e0e3-foreground">
            Fecha de la cotización
            <input className={`${campo} mt-1 w-full`} type="date" value={cotizacion.fecha} onChange={(event) => setCotizacion({ ...cotizacion, fecha: event.target.value })} />
          </label>
          <label className="flex items-center gap-2 text-sm text-e0e3-foreground">
            <input type="checkbox" checked={cotizacion.verificable} onChange={(event) => setCotizacion({ ...cotizacion, verificable: event.target.checked })} />
            La cotización está verificada
          </label>
        </div>
        <Button color="primary" onClick={comparar} disabled={comparando}>
          {comparando ? 'Comparando…' : 'Comparar'}
        </Button>
      </section>

      {resultado ? (
        <>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <label className="text-sm text-e0e3-foreground">
              Familia
              <select className={`${campo} mt-1 w-full`} value={filtroFamilia} onChange={(event) => setFiltroFamilia(event.target.value)}>
                <option value="">Todas</option>
                {familias.map((familia) => <option key={familia} value={familia}>{familia}</option>)}
              </select>
            </label>
            <label className="text-sm text-e0e3-foreground">
              Proveedor
              <select className={`${campo} mt-1 w-full`} value={filtroProveedor} onChange={(event) => setFiltroProveedor(event.target.value)}>
                <option value="">Todos</option>
                {[...new Set(listas.map((item) => item.proveedor_id))].map((id) => (
                  <option key={id} value={id}>{nombreProveedor(id)}</option>
                ))}
              </select>
            </label>
            <label className="text-sm text-e0e3-foreground">
              Equivalencia
              <select className={`${campo} mt-1 w-full`} value={filtroVista} onChange={(event) => setFiltroVista(event.target.value)}>
                <option value="todas">Todas</option>
                <option value="comparables">Comparables</option>
                <option value="no">No comparables</option>
              </select>
            </label>
          </div>

          {!verCostos ? (
            <p className="text-sm text-e0e3-muted">Tu usuario no recibe el costo ni el margen del POS.</p>
          ) : null}

          {filtroVista !== 'no' ? grupos.map((grupo) => (
            <article key={`${grupo.producto_id}-${grupo.calidad}`} className="nexo-surface space-y-3 rounded-2xl p-4">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <h2 className="font-semibold text-e0e3-foreground">{grupo.producto_nombre || grupo.producto_id}</h2>
                  <p className="text-sm text-e0e3-muted">Calidad {grupo.calidad} · {grupo.familia || 'sin familia'} · unidad {grupo.unidad_comparacion || 'no informada'}</p>
                </div>
                <label className="text-sm text-e0e3-foreground">
                  Cantidad a simular
                  <input
                    className={`${campo} mt-1 w-full sm:w-32`}
                    inputMode="decimal"
                    value={cantidades[`${grupo.producto_id}::${grupo.calidad}`] || ''}
                    onChange={(event) => setCantidades({
                      ...cantidades,
                      [`${grupo.producto_id}::${grupo.calidad}`]: event.target.value
                    })}
                  />
                </label>
              </div>
              {grupo.advertencias?.map((item) => (
                <p key={item.code} className="text-sm text-e0e3-muted">{item.message}</p>
              ))}
              {grupo.ganador_precio_lista ? (
                <p className="text-sm text-e0e3-foreground">
                  Precio de lista más bajo: {grupo.ganador_precio_lista.proveedor_nombre || grupo.ganador_precio_lista.proveedor_id} · {dinero(grupo.ganador_precio_lista.precio_normalizado)} {resultado.moneda_objetivo}. {grupo.ganador_precio_lista.aclaracion}
                </p>
              ) : (
                <p className="text-sm text-e0e3-muted">No hay un ganador de precio de lista.</p>
              )}
              {grupo.ganador_costo_efectivo ? (
                <p className="text-sm text-e0e3-foreground">
                  Costo efectivo estimado más bajo: {grupo.ganador_costo_efectivo.proveedor_nombre || grupo.ganador_costo_efectivo.proveedor_id} · {dinero(grupo.ganador_costo_efectivo.costo_efectivo)} {resultado.moneda_objetivo}. No es una calidad.
                </p>
              ) : null}
              {verCostos && grupo.costo_pos !== null && grupo.costo_pos !== undefined ? (
                <p className="text-sm text-e0e3-foreground">
                  Costo actual del POS: {dinero(grupo.costo_pos)}
                  {grupo.diferencia_vs_costo !== null && grupo.diferencia_vs_costo !== undefined
                    ? ` · diferencia ${dinero(grupo.diferencia_vs_costo)} (${dinero(grupo.diferencia_vs_costo_pct)}%)`
                    : ''}
                </p>
              ) : null}
              {verMargenes && grupo.margen ? (
                <p className="text-sm text-e0e3-foreground">
                  Margen simulado con el motor de ventas: {dinero(grupo.margen.margen_bruto_pct)}%. No cambia el costo del producto.
                </p>
              ) : null}
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                {grupo.ofertas.map((oferta) => (
                  <div key={`${oferta.lista_id}-${oferta.codigo_externo}`} className="rounded-xl border border-e0e3-border p-3 text-sm">
                    <p className="font-medium text-e0e3-foreground">{oferta.proveedor_nombre || oferta.proveedor_id}</p>
                    <p className="text-e0e3-muted">{oferta.nombre} · {oferta.codigo_externo} · versión {oferta.version}</p>
                    <p className="text-e0e3-foreground">Precio de lista: {dinero(oferta.precio_lista)} {oferta.moneda || ''}</p>
                    <p className="text-e0e3-foreground">Precio normalizado: {dinero(oferta.precio_normalizado)} {resultado.moneda_objetivo}</p>
                    <p className="text-e0e3-foreground">Costo efectivo estimado: {oferta.costo_efectivo === null ? 'no calculado' : dinero(oferta.costo_efectivo)}</p>
                    {oferta.diferencia_absoluta !== null && oferta.diferencia_absoluta !== undefined ? (
                      <p className="text-e0e3-muted">Contra el más bajo: {dinero(oferta.diferencia_absoluta)} ({dinero(oferta.diferencia_porcentual)}%)</p>
                    ) : null}
                    {oferta.motivos?.map((motivo) => (
                      <p key={motivo.code} className="text-e0e3-muted">{motivo.message}</p>
                    ))}
                  </div>
                ))}
              </div>
            </article>
          )) : null}

          {filtroVista !== 'comparables' ? (
            <section className="nexo-surface space-y-2 rounded-2xl p-4">
              <h2 className="font-semibold text-e0e3-foreground">No comparables</h2>
              {!noComparables.length ? <p className="text-sm text-e0e3-muted">No hay productos fuera de comparación.</p> : null}
              {noComparables.map((item) => (
                <p key={`${item.lista_id}-${item.codigo_externo}-${item.motivo}`} className="text-sm text-e0e3-foreground">
                  {item.nombre || item.codigo_externo} · {item.proveedor_nombre || item.proveedor_id} · {item.motivo} · lista {dinero(item.precio_lista)}
                </p>
              ))}
            </section>
          ) : null}

          <section className="nexo-surface space-y-2 rounded-2xl p-4">
            <h2 className="font-semibold text-e0e3-foreground">Historial de variaciones</h2>
            {!variaciones.length ? <p className="text-sm text-e0e3-muted">Elegí más de una versión del mismo proveedor para ver el cambio.</p> : null}
            {variaciones.map((item) => (
              <p key={`${item.proveedor_id}-${item.codigo_externo}-${item.version_actual}`} className="text-sm text-e0e3-foreground">
                {etiquetaSentido(item.sentido)} · {item.nombre || item.codigo_externo} · {dinero(item.precio_anterior)} → {dinero(item.precio_actual)}
                {item.diferencia_absoluta !== null ? ` · ${dinero(item.diferencia_absoluta)} (${dinero(item.diferencia_porcentual)}%)` : ''}
                <span className="block text-e0e3-muted">{String(item.fecha_anterior || '').slice(0, 10)} → {String(item.fecha_actual || '').slice(0, 10)}</span>
              </p>
            ))}
          </section>

          <section className="nexo-surface space-y-2 rounded-2xl p-4">
            <h2 className="font-semibold text-e0e3-foreground">Resumen de la simulación</h2>
            {!resultado.simulacion ? <p className="text-sm text-e0e3-muted">Cargá una cantidad y volvé a comparar. No se genera una compra.</p> : null}
            {resultado.simulacion?.limitaciones?.map((texto) => (
              <p key={texto} className="text-sm text-e0e3-muted">{texto}</p>
            ))}
            {resultado.simulacion?.opciones?.map((opcion) => (
              <div key={`${opcion.tipo}-${opcion.proveedor_id || opcion.nombre}`} className="text-sm text-e0e3-foreground">
                <p>
                  {opcion.nombre}: {opcion.total === null ? 'total no calculado' : dinero(opcion.total)} {resultado.moneda_objetivo}
                  {opcion.cumple_compra_minima === false ? ' · no cumple la compra mínima' : ''}
                </p>
                {opcion.detalle?.map((linea) => (
                  <p key={`${linea.producto_id}-${linea.codigo_externo}`} className="text-e0e3-muted">
                    Pedida {dinero(linea.cantidad_pedida)} · adquirida {dinero(linea.cantidad_adquirida)}
                    {linea.sobrante === null || linea.sobrante === undefined
                      ? ' · sobrante no informado'
                      : ` · sobrante ${dinero(linea.sobrante)}`}
                    {linea.presentaciones ? ` · ${linea.presentaciones} presentación(es)` : ''}
                  </p>
                ))}
              </div>
            ))}
            {resultado.simulacion?.mejor_opcion_verificada ? (
              <p className="text-sm text-e0e3-foreground">
                Mejor opción verificada: {resultado.simulacion.mejor_opcion_verificada.nombre} · {dinero(resultado.simulacion.mejor_opcion_verificada.total)} {resultado.moneda_objetivo}.
                {resultado.simulacion.optimizacion_completa ? ' La optimización de estas líneas está completa.' : ' La optimización no está completa.'}
              </p>
            ) : null}
            {resultado.simulacion?.diferencia_economica !== null && resultado.simulacion?.diferencia_economica !== undefined ? (
              <p className="text-sm text-e0e3-foreground">Diferencia con la opción siguiente: {dinero(resultado.simulacion.diferencia_economica)}</p>
            ) : null}
            {resultado.simulacion?.ahorro_potencial !== null && resultado.simulacion?.ahorro_potencial !== undefined ? (
              <p className="text-sm text-e0e3-foreground">Ahorro potencial contra la opción verificada más cara: {dinero(resultado.simulacion.ahorro_potencial)}</p>
            ) : null}
            {verMargenes && resultado.simulacion?.impacto_margen ? (
              <p className="text-sm text-e0e3-foreground">
                Margen simulado de la opción: {dinero(resultado.simulacion.impacto_margen.margen_oferta)}%.
                Contra el costo actual: {dinero(resultado.simulacion.impacto_margen.diferencia_margen_pct)} puntos.
              </p>
            ) : null}
          </section>
        </>
      ) : null}
    </div>
  );
}
